import type { ElementVersionRevisionHistory } from "@v1/types/core-state";
import type { ReploElementVersionRevision } from "schemas/generated/element";

import * as React from "react";

import { Avatar } from "@replo/design-system/components/avatar/Avatar";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  Group,
  GroupHeader,
  GroupTitle,
  GroupTitleContainer,
} from "@v1/components/common/designSystem/Group";
import VersionCard, {
  formatVersionHistoryDateAndTime,
} from "@v1/components/version-history/VersionCard";
import { ReploElementVersionKinds } from "schemas/element";

type CollapsibleVersionCardProps = {
  versionsGroup: ElementVersionRevisionHistory;
  selectedRevisionId: string | null;
  onItemClick: (item: ReploElementVersionRevision) => void;
  className?: string;
};

const AutosaveVersionsSummary = ({
  version,
}: {
  version: ReploElementVersionRevision;
}) => {
  const { createdBy, createdAt, title } = version;
  const date = formatVersionHistoryDateAndTime(createdAt);
  const userName = createdBy?.name ?? "Anonymous";
  return (
    <div className="corner-[6px] hover:bg-sheet ml-1.5 flex grow flex-col gap-1 rounded-sm p-1.5 py-1">
      <h3 className="text-default truncate text-xs font-medium">{title}</h3>
      <span className="text-muted text-xs">{date}</span>
      <div className="flex items-center">
        <Avatar name={userName} UNSAFE_className="mr-1" isSquare size="xxs" />
        <span className="text-muted text-xs">{userName}</span>
      </div>
    </div>
  );
};

export default function CollapsibleVersionCard({
  versionsGroup,
  selectedRevisionId,
  onItemClick,
  className,
}: CollapsibleVersionCardProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const title = `${versionsGroup.items.length} Autosave versions`;
  const mostRecentItem = versionsGroup.items[versionsGroup.items.length - 1]!;
  return (
    <Group
      key={versionsGroup.items[0]!.id}
      name={`${versionsGroup.items.length} Autosave versions`}
      className={twMerge("pb-6", className)}
      contentClassName="pt-0"
      header={
        <GroupHeader>
          <GroupTitleContainer
            className="grow items-start gap-0"
            collapsibleIndicatorClassName="z-10 rounded-sm overflow-hidden h-[20px] w-[20px] flex items-center justify-center bg-surface"
          >
            {isOpen ? (
              <AutosaveVersionsSummary version={{ ...mostRecentItem, title }} />
            ) : (
              <GroupTitle className="hover:bg-sheet corner-[6px] ml-1.5 grow py-1 pl-1.5" />
            )}
          </GroupTitleContainer>
        </GroupHeader>
      }
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      isCollapsible
    >
      {versionsGroup.items.map((item) => {
        const cardIsActive =
          (item.kind === ReploElementVersionKinds.current &&
            !selectedRevisionId) ||
          selectedRevisionId === item.id;
        return (
          <VersionCard
            key={item.id}
            version={item}
            className="py-1"
            isActive={cardIsActive}
            isDateOnly
            onClick={() => {
              onItemClick(item);
            }}
          />
        );
      })}
    </Group>
  );
}
